import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import './App.css';

const Toaster = lazy(() => import('./components/ui/sonner').then((module) => ({ default: module.Toaster })));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectsGalleryPage = lazy(() => import('./pages/ProjectsGalleryPage'));
const ProjectCaseStudyPage = lazy(() => import('./pages/ProjectCaseStudyPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MediaPage = lazy(() => import('./pages/MediaPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const AcousticRegionPage = lazy(() => import('./pages/AcousticRegionPage'));
const AcousticTreatmentPage = lazy(() => import('./pages/AcousticTreatmentPage'));
const SpecifierLandingPage = lazy(() => import('./pages/SpecifierLandingPage'));
const AcousticPanelsIndiaPage = lazy(() => import('./pages/AcousticPanelsIndiaPage'));
const SoundproofingSolutionsIndiaPage = lazy(() => import('./pages/SoundproofingSolutionsIndiaPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const ClientToaster = () => {
  const [mounted, setMounted] = useState(false);
  const { pathname } = useLocation();
  const shouldLoadToaster = pathname.startsWith('/contact') || pathname.startsWith('/careers');

  useEffect(() => {
    if (shouldLoadToaster && navigator.userAgent !== 'ReactSnap') {
      setMounted(true);
    }
  }, [shouldLoadToaster]);

  return mounted && shouldLoadToaster ? (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  ) : null;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '100vh', backgroundColor: '#12372b' }} />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:productSlug" element={<ProductDetailPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/gallery" element={<ProjectsGalleryPage />} />
              <Route path="/projects/:projectSlug" element={<ProjectCaseStudyPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/media" element={<MediaPage />} />
              <Route path="/media/:newsSlug" element={<NewsDetailPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:blogSlug" element={<BlogDetailPage />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/acoustic-solutions/:regionSlug" element={<AcousticRegionPage />} />
              <Route path="/acoustic-treatment/:segmentSlug" element={<AcousticTreatmentPage />} />
              <Route path="/acoustic-panels-india" element={<AcousticPanelsIndiaPage />} />
              <Route path="/soundproofing-solutions-india" element={<SoundproofingSolutionsIndiaPage />} />
              <Route path="/acoustic-panel-manufacturer-india" element={<SpecifierLandingPage pageKey="manufacturerIndia" />} />
              <Route path="/architectural-acoustics-india" element={<SpecifierLandingPage pageKey="architecturalAcousticsIndia" />} />
              <Route path="/acoustic-ceiling-systems-india" element={<SpecifierLandingPage pageKey="ceilingSystemsIndia" />} />
              <Route path="/acoustic-panels-bangalore" element={<SpecifierLandingPage pageKey="bangaloreAcousticPanels" />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <ClientToaster />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
